import React from 'react';

/**
 * KPIStripLayout — Shared analytical KPI grid.
 *
 * Props:
 * - children: ReactNode — KPIItem components
 * - className?: string — extra className
 */
// Column count follows the number of CARDS, up to four. It was hardcoded to
// lg:grid-cols-4, so a strip that had been trimmed to one card left that card at a
// quarter width with three empty columns beside it -- which reads as three tiles that
// failed to load rather than as a deliberate single metric. DocumentsKPIStrip is in
// exactly that state: three of its four cards were removed on 2026-08-28 as duplicates
// of counts DocumentsRiskPanel already showed.
//
// Nothing changes for a full strip: four children still resolve to lg:grid-cols-4.
// null/false children are not counted, so a conditionally-rendered card cannot leave a
// gap either.
const LG_COLS = { 1: 'lg:grid-cols-1', 2: 'lg:grid-cols-2', 3: 'lg:grid-cols-3' };

const KPIStripLayout = ({ children, className = '' }) => {
  const count = React.Children.toArray(children).length;
  const lgCols = LG_COLS[count] || 'lg:grid-cols-4';
  const smCols = count === 1 ? 'sm:grid-cols-1' : 'sm:grid-cols-2';
  return (
    <div className={`grid grid-cols-1 gap-3 ${smCols} ${lgCols} ${className}`}>
      {children}
    </div>
  );
};

/**
 * KPIItem — Individual KPI display card for use inside KPIStripLayout.
 *
 * Props:
 * - label: string — KPI name
 * - value: string|number — the metric value
 * - suffix?: string — unit suffix (e.g. '%', 'days')
 * - icon?: HeroIcon component OR emoji string
 * - gradient?: string — Tailwind gradient classes for the icon bg (e.g. 'from-blue-500 to-blue-600')
 * - subtext?: string — optional description below value
 * - onClick?: () => void
 * - textColor?: string — override text color for value
 */
export const KPIItem = ({
  label,
  value,
  suffix,
  icon: Icon,
  gradient = 'from-blue-500 to-blue-600',
  subtext,
  onClick,
  textColor = 'text-gray-900 dark:text-white',
  loading = false,
  change,
  changeType,
  trend = [],
}) => {
  const isEmoji = typeof Icon === 'string';
  const Element = onClick ? 'button' : 'div';
  const trendMaximum = Math.max(...trend, 1);

  return (
    <Element
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      className={`group relative min-w-0 overflow-hidden rounded-2xl border border-slate-200/80 bg-white/70 p-4 text-left shadow-sm backdrop-blur-xl transition-all duration-200 dark:border-slate-700/60 dark:bg-slate-900/65 ${onClick ? 'cursor-pointer hover:-translate-y-0.5 hover:border-primary-400/60 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary-500/60' : ''}`}
    >
      <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${gradient}`} aria-hidden="true"></div>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-xs font-semibold uppercase leading-tight tracking-wide text-slate-500 dark:text-slate-300">{label}</div>
          {loading ? (
            <div className="mt-2 h-7 w-24 animate-pulse rounded-lg bg-slate-200 dark:bg-slate-700"></div>
          ) : (
            <div className={`mt-1 text-2xl font-bold ${textColor}`}>
              {value}
              {suffix && <span className="ml-1 text-sm font-normal text-slate-500 dark:text-slate-400">{suffix}</span>}
            </div>
          )}
        </div>
        {isEmoji ? (
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-lg dark:bg-slate-800" aria-hidden="true">{Icon}</span>
        ) : Icon ? (
          <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br ${gradient} shadow-sm`}>
            <Icon className="h-5 w-5 text-white" aria-hidden="true" />
          </div>
        ) : null}
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {subtext && <div className="text-xs text-slate-500 dark:text-slate-400">{subtext}</div>}
        {change && (
          <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${changeType === 'increase' ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300' : changeType === 'decrease' ? 'bg-rose-500/15 text-rose-700 dark:text-rose-300' : 'bg-slate-500/15 text-slate-600 dark:text-slate-300'}`}>
            {change}
          </span>
        )}
      </div>
      {trend.length > 0 && (
        <div className="mt-3 flex h-7 items-end gap-1" aria-hidden="true">
          {trend.map((point, index) => (
            <span key={index} className={`w-1.5 rounded-sm bg-gradient-to-t ${gradient} opacity-65`} style={{ height: `${Math.max(10, (point / trendMaximum) * 100)}%` }}></span>
          ))}
        </div>
      )}
    </Element>
  );
};

export default KPIStripLayout;
